import type {ChangeEvent, ReactElement} from 'react';
import {useCallback, useState} from 'react';
import {EMPTY_SEARCH_QUERY, MAC_CMD_GLYPH, MAC_DELETE_KEY, SHORTCUT_LIST_SEPARATOR, SHORTCUT_SEPARATOR, SHORTCUTS_GENERAL, SHORTCUTS_TEXT} from './keyboard-shortcuts-view.types';
import type {KeyboardShortcutsViewProps, ShortcutItem, ShortcutSection} from './keyboard-shortcuts-view.types';
import {RightPanelOverlayHeader} from '../../components/right-panel-overlay-header/right-panel-overlay-header';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import styles from './keyboard-shortcuts-view.module.scss';

function isMacOS(): boolean {
  return navigator.userAgent.includes('Mac');
}

/**
 * Resolves the display keys for a shortcut string. A two-alternative string picks the Mac or
 * non-Mac variant; a single bare key is returned as-is; a single combo is split on the separator.
 */
function getRelevantKeys(keys: string): readonly string[] {
  const isMac = isMacOS();
  const alternatives = keys.split(SHORTCUT_LIST_SEPARATOR);

  if (alternatives.length === 1) {
    const single = alternatives[0];
    if (single === undefined) {
      return [];
    }
    if (single.length === 1) {
      return [single];
    }
    return single.split(SHORTCUT_SEPARATOR);
  }

  const isMacShortcut = (s: string): boolean => s.includes(MAC_CMD_GLYPH) || s.includes(MAC_DELETE_KEY);
  const filter = isMac ? isMacShortcut : (s: string): boolean => !isMacShortcut(s);
  const chosen = alternatives.find(filter);
  return chosen?.split(SHORTCUT_SEPARATOR) ?? [];
}

function toTitleCase(str: string): string {
  return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
}

const SHORTCUT_SECTIONS: readonly ShortcutSection[] = [
  {headingKey: 'pmwjs_general', shortcuts: SHORTCUTS_GENERAL},
  {headingKey: 'pmwjs_text', shortcuts: SHORTCUTS_TEXT},
];

/** Case-insensitive substring filter against the resolved label so the match runs on displayed text; an empty query passes every row. */
function filterShortcuts(shortcuts: readonly ShortcutItem[], query: string): readonly ShortcutItem[] {
  const normalizedQuery = query.toLowerCase();
  return shortcuts.filter((item) => window.i18next.t(item.labelKey).toLowerCase().includes(normalizedQuery));
}

export function KeyboardShortcutsView({onBack}: KeyboardShortcutsViewProps): ReactElement {
  const [searchQuery, setSearchQuery] = useState<string>(EMPTY_SEARCH_QUERY);

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>): void => {
    setSearchQuery(e.target.value);
  }, []);

  return (
    <div className={styles.view}>
      <RightPanelOverlayHeader title={window.i18next.t('pmwjs_keyboard_shortcuts')} backAriaLabel={window.i18next.t('pmwjs_back')} onBack={onBack} />
      {renderSearch(searchQuery, handleSearchChange)}
      {SHORTCUT_SECTIONS.map((section) => renderFilteredSection(section, searchQuery))}
    </div>
  );
}

function renderSearch(value: string, onChange: (e: ChangeEvent<HTMLInputElement>) => void): ReactElement {
  const placeholder = window.i18next.t('pmwjs_search_keyboard_shortcut_ellipses');
  return (
    <div className={styles.searchContainer}>
      <input aria-label={placeholder} className={styles.searchInput} onChange={onChange} placeholder={placeholder} type="search" value={value} />
      <Icon ariaLabel="" className={styles.searchIcon} icon="icon-search" size={IconSize.SIZE_ICON_20} />
    </div>
  );
}

function renderFilteredSection(section: ShortcutSection, query: string): ReactElement | null {
  const filtered = filterShortcuts(section.shortcuts, query);
  if (filtered.length === 0) {
    return null;
  }
  return renderSection(section.headingKey, window.i18next.t(section.headingKey), filtered);
}

function renderSection(key: string, heading: string, shortcuts: readonly ShortcutItem[]): ReactElement {
  return (
    <section key={key} className={styles.section}>
      <h3 className={styles.sectionHeading}>{heading}</h3>
      <ul className={styles.list}>{shortcuts.map((item) => renderShortcutItem(item))}</ul>
    </section>
  );
}

function renderShortcutItem(item: ShortcutItem): ReactElement {
  const keys = getRelevantKeys(item.keys);
  return (
    <li key={item.labelKey} className={styles.item}>
      <Text val={window.i18next.t(item.labelKey)} size={TextSize.MD} className={styles.itemLabel} />
      <div className={styles.shortcutGroup}>{keys.map((key, idx) => renderKeyPart(key, idx, keys.length))}</div>
    </li>
  );
}

function renderPlusSeparator(idx: number, total: number): ReactElement | null {
  if (idx >= total - 1) {
    return null;
  }
  return <Icon aria-hidden className={styles.plusIcon} icon="icon-plus" size={IconSize.SIZE_ICON_16} />;
}

function renderKeyPart(key: string, idx: number, total: number): ReactElement {
  const isString = key.length > 1;
  const boxClass = isString ? styles.stringBox : styles.charBox;
  return (
    <div key={`${key}-${idx}`}>
      <Text val={toTitleCase(key)} size={TextSize.MD} bold className={boxClass} />
      {renderPlusSeparator(idx, total)}
    </div>
  );
}
